/**
 * English shown beside Portuguese the app surfaces.
 * The Portuguese stays the primary text. Geographic proper names
 * (Carvoeiro, Lagoa, Portimão, Rocha Brava, Benagil, and the like) are not glossed.
 */

const EXACT = new Map<string, string>();

function workingCopy(value: string): string {
  return value
    .replace(/\u00a0/g, ' ')
    .replace(/[\u2013\u2014]/g, '-')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Same key the gloss map uses, so a fix can match the title the agenda sent. */
export function translationKey(value: string): string {
  return workingCopy(value);
}

function tidy(value: string): string {
  return value.replace(/[ ]{2,}/g, ' ').trim();
}

function unchanged(original: string, translated: string): boolean {
  return tidy(original).localeCompare(tidy(translated), 'en', { sensitivity: 'accent' }) === 0;
}

function addExact(portuguese: string, english: string) {
  EXACT.set(workingCopy(portuguese), english);
}

addExact('O Castelo', 'The Castle');
addExact('Farmácia Carvoeiro', 'Carvoeiro Pharmacy');
addExact('Farmácia Rocha Brava', 'Rocha Brava Pharmacy');
addExact('Minimercado Rocha Brava', 'Rocha Brava mini-market');
addExact('Rei das Praias', 'King of the Beaches');
addExact('Pingo Doce Lagoa', 'Sweet Drop supermarket, Lagoa');
addExact('Mercado Municipal de Lagoa', 'Lagoa Municipal Market');
addExact('Hospital de Portimão', 'Portimão Hospital');
addExact('Hospital Portimão', 'Portimão Hospital');
addExact('Boneca Bar', 'Doll Bar');
addExact('Gelataria Veneza', 'Venice ice-cream shop');
addExact('A Lanterna', 'The Lantern');
addExact('Pizzaria La Piazza', 'La Piazza pizzeria');
addExact('O Capelo', 'The Cap');
addExact('Minipreço Carvoeiro', 'Mini Price supermarket, Carvoeiro');
addExact('Carvoeiro Clube de Ténis', 'Carvoeiro Tennis Club');
addExact('Nossa Senhora da Encarnação', 'Our Lady of the Incarnation');
addExact('Nossa Senhora da Rocha', 'Our Lady of the Rock');
addExact('Clínica Dentária Carvoeiro', 'Carvoeiro Dental Clinic');
addExact('Castelo de Silves', 'Silves Castle');
addExact('Olaria Pequena / Porches pottery', 'Small pottery workshop / Porches pottery');
addExact('Continente Modelo Lagoa', 'Mid-size Continente supermarket, Lagoa');
addExact('Centro de Saúde de Lagoa', 'Lagoa Health Centre');
addExact('Hospital Particular São Gonçalo', 'Saint Gonçalo Private Hospital');
addExact('Quinta dos Vales', 'Estate of the Valleys');
addExact('GNR Lagoa', 'Lagoa National Republican Guard');
addExact('Bombeiros Voluntários de Lagoa', 'Lagoa Volunteer Firefighters');
addExact('Adega Cooperativa de Lagoa', 'Lagoa Cooperative Winery');
addExact('O Farol', 'The Lighthouse');
addExact('Café e Bar', 'Café and Bar');

addExact('Mês Sénior 2026', 'Senior Month 2026');
addExact('Mês da Igualdade', 'Equality Month');
addExact('Os Brincos à Ronaldo e Outras Histórias', 'The Ronaldo Earrings and Other Stories');
addExact('Sunset Jovem', 'Youth Sunset');
addExact('Alma Flamenca', 'Flamenco Soul');
addExact('O Som e a Fúria', 'The Sound and the Fury');
addExact('A Magia das 1001 Noites', 'The Magic of the 1001 Nights');
addExact('Formação +Próxima | Outubro', 'Training +Next | October');
addExact('A Minha Terra Linda', 'My Beautiful Land');
addExact(
  'Prémio Internacional de Arquitetura Luiz Conceição [5ª edição]',
  'Luiz Conceição International Architecture Prize [5th edition]',
);
addExact('Objetos que Contam Histórias', 'Objects That Tell Stories');
addExact('Exposição Coletiva de Pintura | Celebração - Celebration', 'Group Painting Exhibition | Celebration');
addExact('Ações de Formação | Centro Qualifica de Lagoa', 'Training courses | Lagoa Qualifica Centre');
addExact(
  'Parque Natural Marinho do Recife do Algarve - Pedra do Valado',
  'Algarve Reef Marine Natural Park – Pedra do Valado',
);
addExact('Voar com a Música', 'Flying with the Music');
addExact('Lagoa a Rolar', 'Lagoa Rolling');
addExact('Oficina Arte & Afeto', 'Art & Affection Workshop');
addExact(
  'Centro Municipal de Marcha Corrida Lagoa - Época 2026/2027',
  'Lagoa Municipal Walking and Running Centre – 2026/2027 season',
);
addExact('Torneio Sénior de Walking Football', 'Senior Walking Football Tournament');
addExact('Apresentação do Livro Samuel', 'Presentation of the Book Samuel');
addExact('Contos que não se devem contar!', 'Stories That Should Not Be Told!');
addExact('Polly Gibbons & Orquestra de Jazz do Algarve', 'Polly Gibbons & the Algarve Jazz Orchestra');
addExact('Concerto de Janeiro', 'January Concert');
addExact('Semana de Janeiro', 'January Week');
addExact('Centro de Congressos de Lagoa', 'Lagoa Conference Centre');
addExact('Concelho de Lagoa', 'Lagoa municipal area');
addExact('Município de Lagoa', 'Municipality of Lagoa');
addExact('Auditório Carlos do Carmo', 'Carlos do Carmo Auditorium');
addExact('Convento Bio', 'Bio Convent');
addExact('Consultar Programa', 'See the program');
addExact('Patrulha do Recife', 'Reef Patrol');
addExact('Condicionamento Temporário de Circulação Rodoviária', 'Temporary Road Traffic Restrictions');

function ordinal(n: number): string {
  const mod100 = n % 100;
  if (mod100 >= 11 && mod100 <= 13) return `${n}th`;
  switch (n % 10) {
    case 1:
      return `${n}st`;
    case 2:
      return `${n}nd`;
    case 3:
      return `${n}rd`;
    default:
      return `${n}th`;
  }
}

/** Street types, beach words, and a few place-name phrases. Longer phrases first. */
function applyPatterns(input: string): string {
  let s = input;
  const pair = (pattern: RegExp, replacement: string) => {
    pattern.lastIndex = 0;
    s = s.replace(pattern, replacement);
  };
  const named = (pattern: RegExp, format: (name: string) => string) => {
    pattern.lastIndex = 0;
    s = s.replace(pattern, (_match, name: string) => format(name.trim()));
  };

  pair(/\bUrbanização\s+([^,]+)/g, '$1 residential development');
  pair(/\bUrb\.\s+([^,]+)/g, '$1 residential development');
  pair(/\bAeroporto de Faro\b/g, 'Faro Airport');
  pair(/\bApartado\b/g, 'P.O. Box');
  pair(/\bEstrada Nacional\b/g, 'National Road');
  pair(/\bEstrada do Farol\b/g, 'Lighthouse Road');
  pair(/\bEstrada de\s+([^,]+)/g, '$1 Road');
  pair(/\bLargo da Praia\b/g, 'Beach Square');
  pair(/\bLargo 25 de Abril\b/g, '25 April Square');
  pair(/\bRua dos Pescadores\b/g, "Fishermen's Street");
  pair(/\bRua do Barranco\b/g, 'Ravine Street');
  pair(/\bRua do Mercado\b/g, 'Market Street');
  pair(/\bRua do Castelo\b/g, 'Castle Street');
  pair(/\bRua da Escola\b/g, 'School Street');
  pair(/\bRua da Portela\b/g, 'Portela Street');
  pair(/\bRua 5 de Outubro\b/g, '5 October Street');
  pair(/\bRua de São Pedro\b/g, 'Saint Peter Street');
  pair(/\bPraça Infante Dom Henrique\b/g, 'Prince Henry the Navigator Square');
  pair(/\bSítio do Poço Seco\b/g, 'Poço Seco locality');
  pair(/\bSítio das Eiras\b/g, 'Eiras locality');
  pair(/\bSítio dos Barros\b/g, 'Barros locality');
  pair(/\bQuinta do Alto do Poço\b/g, 'Alto do Poço estate');
  pair(/\bPraia de Nossa Senhora da Rocha\b/g, 'Our Lady of the Rock Beach');
  pair(/\bPraia do Vale de Centeanes\b/g, 'Vale de Centeanes Beach');
  pair(/\bPraia de Vale Centeanes\b/g, 'Vale Centeanes Beach');
  pair(/\bPraia de Vale Covo\b/g, 'Vale Covo Beach');
  pair(/\bPraia do Paraíso\b/g, 'Paradise Beach');
  pair(/\bPraia do Carvalho\b/g, 'Oak Beach');
  pair(/\bPraia do Carvoeiro\b/g, 'Carvoeiro Beach');
  pair(/\bPraia dos Caneiros\b/g, 'Channels Beach');
  pair(/\bPraia de Benagil\b/g, 'Benagil Beach');
  pair(/\bPraia da Marinha\b/g, 'Marinha Beach');
  pair(/\bPraia da Rocha\b/g, 'Rock Beach');
  pair(/\bPraia de Albandeira\b/g, 'Albandeira Beach');
  pair(/\bPraia Grande\b/g, 'Big Beach');
  pair(/\bEN125\b/g, 'National Road 125');
  pair(/\bMato Serrão\b/g, 'Serrão scrubland');
  pair(/\bAlgar Seco\b/g, 'Dry sea cave');

  named(/\bAvenida\s+([^,]+?)(?=\s*,|$)/g, (name) => `${name} Avenue`);
  named(/\bRua\s+(?:do|da|de|dos|das)\s+([^,]+?)(?=\s+\d|,|$)/g, (name) => `${name} Street`);
  named(/\bRua\s+([^,]+?)(?=\s+\d|,|$)/g, (name) => `${name} Street`);
  named(/\bLargo\s+(?:do|da|de|dos|das)\s+([^,]+?)(?=\s+\d|,|$)/g, (name) => `${name} Square`);
  named(/\bLargo\s+([^,]+?)(?=\s+\d|,|$)/g, (name) => `${name} Square`);
  pair(/\bLargo\b/g, 'Square');
  named(/\bPraia\s+(?:do|da|de|dos|das)\s+([^,]+?)(?=\s*,|$)/g, (name) => `${name} Beach`);
  named(/\bSítio\s+(?:do|da|de|dos|das)\s+([^,]+?)(?=\s*,|$)/g, (name) => `${name} locality`);
  named(/\bQuinta\s+(?:do|da|de|dos|das)\s+([^,]+?)(?=\s*,|$)/g, (name) => `${name} estate`);
  pair(/\bEstrada\b/g, 'Road');
  pair(/\bPraça\b/g, 'Square');
  return s;
}

/** Municipal wording on What's On titles and venues the exact map does not already name. */
function applyPhrases(input: string): string {
  let s = input;
  const pair = (pattern: RegExp, replacement: string) => {
    pattern.lastIndex = 0;
    s = s.replace(pattern, replacement);
  };

  pair(/Orquestra de Jazz do Algarve/gi, 'the Algarve Jazz Orchestra');
  pair(/Prémio Internacional de Arquitetura/gi, 'International Architecture Prize');
  pair(/Exposição Coletiva de Pintura/gi, 'Group Painting Exhibition');
  pair(/Ações de Formação/gi, 'Training courses');
  pair(/Centro Qualifica de Lagoa/gi, 'Lagoa Qualifica Centre');
  pair(/Centro de Congressos de Lagoa/gi, 'Lagoa Conference Centre');
  pair(/Centro de Congressos/gi, 'Conference Centre');
  pair(/Parque Natural Marinho do Recife do Algarve/gi, 'Algarve Reef Marine Natural Park');
  pair(/Centro Municipal de Marcha Corrida/gi, 'Municipal walking and running centre');
  pair(/Torneio Sénior de Walking Football/gi, 'Senior Walking Football Tournament');
  pair(/Apresentação do Livro/gi, 'Presentation of the book');
  pair(/Mês Sénior/gi, 'Senior Month');
  pair(/Mês da Igualdade/gi, 'Equality Month');
  pair(/Oficina Arte & Afeto/gi, 'Art & Affection Workshop');
  pair(/Voar com a Música/gi, 'Flying with the Music');
  pair(/Lagoa a Rolar/gi, 'Lagoa Rolling');
  pair(/A Minha Terra Linda/gi, 'My Beautiful Land');
  pair(/Os Brincos à Ronaldo e Outras Histórias/gi, 'The Ronaldo Earrings and Other Stories');
  pair(/Sunset Jovem/gi, 'Youth Sunset');
  pair(/Alma Flamenca/gi, 'Flamenco Soul');
  pair(/O Som e a Fúria/gi, 'The Sound and the Fury');
  pair(/A Magia das 1001 Noites/gi, 'The Magic of the 1001 Nights');
  pair(/Contos que não se devem contar!/gi, 'Stories That Should Not Be Told!');
  pair(/Formação \+Próxima/gi, 'Training +Next');
  pair(/Auditório Carlos do Carmo/gi, 'Carlos do Carmo Auditorium');
  pair(/Câmara Municipal de Lagoa/gi, 'Lagoa Municipal Council');
  pair(/Concelho de Lagoa/gi, 'Lagoa municipal area');
  pair(/Município de Lagoa/gi, 'Municipality of Lagoa');
  pair(/Convento Bio/gi, 'Bio Convent');
  pair(/Celebração - Celebration/gi, 'Celebration');
  pair(/Consultar Programa/gi, 'See the program');
  pair(/\bConcerto de\s+([A-Za-zÀ-ÿ]+)/g, '$1 Concert');
  pair(/\bSemana de\s+([A-Za-zÀ-ÿ]+)/g, '$1 Week');
  s = s.replace(/(\d+)ª\s*edição/gi, (_match, n: string) => `${ordinal(Number(n))} edition`);
  // `\b` only sees [A-Za-z0-9_], so it never matches a word that starts with É.
  pair(/(?<![\p{L}\p{N}_])época(?![\p{L}\p{N}_])/giu, 'season');
  pair(/\bJaneiro\b/g, 'January');
  pair(/\bFevereiro\b/g, 'February');
  pair(/\bMarço\b/g, 'March');
  pair(/\bAbril\b/g, 'April');
  pair(/\bMaio\b/g, 'May');
  pair(/\bJunho\b/g, 'June');
  pair(/\bJulho\b/g, 'July');
  pair(/\bAgosto\b/g, 'August');
  pair(/\bSetembro\b/g, 'September');
  pair(/\bOutubro\b/g, 'October');
  pair(/\bNovembro\b/g, 'November');
  pair(/\bDezembro\b/g, 'December');
  pair(/\bConcerto\b/g, 'Concert');
  pair(/\bSemana\b/g, 'Week');
  pair(/\bExposição\b/g, 'Exhibition');
  pair(/\bFormação\b/g, 'Training');
  pair(/\bOficina\b/g, 'Workshop');
  pair(/\bAuditório\b/g, 'Auditorium');
  pair(/\bSénior\b/g, 'Senior');
  pair(/\bMunicípio\b/g, 'Municipality');
  pair(/\bHospital Portimão\b/g, 'Portimão Hospital');
  pair(/Patrulha do Recife/gi, 'Reef Patrol');
  pair(/Condicionamento Temporário de Circulação Rodoviária/gi, 'Temporary Road Traffic Restrictions');
  // Single words, after the full phrases, so "Patrulha do Recife" stays "Reef Patrol".
  pair(/(?<![\p{L}\p{N}_])patrulha(?![\p{L}\p{N}_])/giu, 'patrol');
  pair(/(?<![\p{L}\p{N}_])recife(?![\p{L}\p{N}_])/giu, 'reef');
  pair(/(?<![\p{L}\p{N}_])circulação(?![\p{L}\p{N}_])/giu, 'traffic');
  pair(/(?<![\p{L}\p{N}_])rodoviári[oa](?![\p{L}\p{N}_])/giu, 'road');
  pair(/(?<![\p{L}\p{N}_])temporári[oa](?![\p{L}\p{N}_])/giu, 'temporary');
  pair(/(?<![\p{L}\p{N}_])condicionamento(?![\p{L}\p{N}_])/giu, 'restriction');
  return s;
}

const INLINE: { source: string; flags: string; english: string }[] = [
  { source: '\\bHospital de Portimão\\b', flags: 'g', english: 'Portimão Hospital' },
  { source: '\\bHospital Portimão\\b', flags: 'g', english: 'Portimão Hospital' },
  { source: '\\bBoneca Bar\\b', flags: 'g', english: 'Doll Bar' },
  { source: '\\bAlgar Seco\\b', flags: 'g', english: 'dry sea cave' },
  { source: '\\bCataplana\\b', flags: 'g', english: 'copper-pan seafood' },
  { source: '\\bmoscatel\\b', flags: 'gi', english: 'muscatel' },
  { source: '\\bCHUA\\b', flags: 'g', english: 'University Hospital Centre of the Algarve' },
  { source: '\\bFarmácia\\b', flags: 'g', english: 'pharmacy' },
  { source: '\\bContinente\\b', flags: 'g', english: 'supermarket' },
  { source: '\\bEN125\\b', flags: 'g', english: 'National Road 125' },
];

function glossOne(input: string, source: string, flags: string, english: string): string {
  const paren = ` (${english})`;
  const re = new RegExp(source, flags.includes('g') ? flags : `${flags}g`);
  let out = '';
  let last = 0;
  for (const match of input.matchAll(re)) {
    const index = match.index ?? 0;
    out += input.slice(last, index);
    const followed = input.slice(index + match[0].length, index + match[0].length + paren.length) === paren;
    out += followed ? match[0] : `${match[0]}${paren}`;
    last = index + match[0].length;
  }
  out += input.slice(last);
  return out;
}

/** Full-string English, or null when the text is already English or a proper name. */
export function englishBeside(text: string): string | null {
  const key = workingCopy(text);
  if (!key) return null;
  const exact = EXACT.get(key);
  if (exact && !unchanged(key, exact)) return exact;
  const next = tidy(applyPhrases(applyPatterns(key)));
  if (!unchanged(key, next)) return next;
  return null;
}

/** Parenthetical English for a Portuguese word inside an otherwise English sentence. */
export function glossInline(text: string): string {
  return INLINE.reduce((next, item) => glossOne(next, item.source, item.flags, item.english), text);
}

/** Keep a screen-reader label and append the English gloss of `source`. */
export function appendEnglish(label: string, source = label): string {
  const english = englishBeside(source);
  if (!english) return label;
  const suffix = `(${english})`;
  if (label.endsWith(suffix)) return label;
  return `${label} ${suffix}`;
}
